import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useApiQuery, usePaginatedApiQuery } from "../../../hooks";
import { useTestRunStepsSSE } from "../../../hooks/use-test-run-steps-sse";
import type { Test, TestContext, TestRun, TestRunStep } from "../../../types";

export function useTestDetails() {
  const { enqueueSnackbar } = useSnackbar();
  const nav = useNavigate();
  const { testId } = useParams();

  const { data: test, refetch: refetchTest } = useApiQuery<Test>(`/tests/${testId}`, {
    enabled: !!testId,
  });

  const { data: ctxs, refetch: refetchContext } = useApiQuery<TestContext[]>(
    `/tests/${testId}/context`,
    {
      enabled: !!testId,
    },
  );

  const { data: runs } = usePaginatedApiQuery<TestRun>(`/runs?test_id=${testId}`, {
    page: 0,
    pageSize: 1,
  });

  const runId = runs?.[0]?.id;
  const isRunning = ["running", "pending"].includes(runs?.[0]?.run_status || "");

  // Initial fetch (for completed runs or page load)
  const { data: initialSteps } = useApiQuery<TestRunStep[]>(`/runs/${runId}/steps`, {
    enabled: !!runId,
  });

  // Real-time SSE updates (only when running)
  const { steps: liveSteps, isConnected } = useTestRunStepsSSE(runId, isRunning);

  // Memoized merge: SSE overrides initial data
  const steps = useMemo(() => {
    if (!isRunning || !isConnected) return initialSteps || [];

    // Merge: create map from initial, then apply live updates
    const merged = new Map((initialSteps || []).map((s) => [s.id, s]));
    liveSteps.forEach((s) => {
      merged.set(s.id, s);
    });

    // Sort by created_at descending (newest first)
    return Array.from(merged.values()).sort((a, b) =>
      (b.created_at || "").localeCompare(a.created_at || ""),
    );
  }, [initialSteps, liveSteps, isRunning, isConnected]);

  const getStepInfo = useCallback(
    (ctx: TestContext) => {
      if (!steps) return null;
      return steps.find((s) => s.block_id === ctx?.data?.id) || null;
    },
    [steps],
  );

  const [selectedCtx, setSelectedCtx] = useState<TestContext | null>(null);

  useEffect(() => {
    if (ctxs && ctxs.length > 0 && !selectedCtx) {
      setSelectedCtx(ctxs[0]);
    }
  }, [ctxs, selectedCtx]);

  useEffect(() => {
    if (!ctxs || ctxs.length === 0 || !selectedCtx) return;

    const g = ctxs.find((ctx) => ctx.id === selectedCtx.id) || null;
    setSelectedCtx(g);
  }, [ctxs, selectedCtx]);

  return {
    test,
    ctxs,
    steps,
    selectedCtx,
    setSelectedCtx,
    getStepInfo,
    refetchTest,
    refetchContext,
    enqueueSnackbar,
    nav,
  };
}
